import React, { useEffect, useState } from "react";
import axios from "axios";
import { Cog, Save, X } from "lucide-react";
import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { useSelector } from "react-redux";
import ErrorNotification from "../../ErrorNotification";
import AddEndpointGroupForm from "./AddEndpointGroupForm";
import EditEndpointGroupForm from "./EditEndpointGroupForm";

const apiUrl = process.env.REACT_APP_API_URL;

export default function EndpointGroupTable({ error, setError }) {
  const [open, setOpen] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true); // Add loading state
  const [selectedGroupName, setSelectedGroupName] = useState("");
  const [selectedGroupId, setSelectedGroupId] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [tooltip, setTooltip] = useState({
    visible: false,
    x: 0,
    y: 0,
    text: "",
  });
  const [showAll, setShowAll] = useState(false);

  const [recordToDelete, setRecordToDelete] = useState({
    id: null,
    groupName: null,
    timeInterval: null,
  });

  let accessToken = useSelector((state) => state.accessToken.value);

  const handleClickOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleClickOpenEdit = (groupName, id) => {
    setSelectedGroupId(id);
    setSelectedGroupName(groupName);
    setOpenEdit(true);
  };
  const handleCloseEdit = () => setOpenEdit(false);

  const getDetails = async () => {
    setLoading(true); // Set loading to true when starting fetch
    try {
      const query = `
      query MyQuery {
        getGroups {
          id
          GroupName
          TimeInterval
        }
      }`;

      const response = await axios.post(
        apiUrl,
        { query },
        {
          headers: {
            Authorization: accessToken,
          },
        }
      );

      if (response.data.data && response.data.data.getGroups) {
        setData(response.data.data.getGroups);
      }
      if (
        response.data.errors &&
        response.data.errors[0].errorType == "Unauthorized"
      )
        setError("Unauthorized");
    } catch (err) {
      setError("Failed to load data");
    } finally {
      setLoading(false); // Set loading to false when done
    }
  };

  useEffect(() => {
    getDetails();
  }, [refresh]);

  const handleDeleteConfirmation = (id, recordName, timeInterval) => {
    setRecordToDelete({
      id: id,
      groupName: recordName,
      timeInterval: timeInterval,
    });
    setOpenDialog(true);
  };

  const deleteRecord = async () => {
    setIsSubmitting(true);
    const query = `
      mutation MyMutation($id:String!,$groupName:String!,$timeInterval:String!) {
        updateSchedule(task: "remove", groupName:$groupName , id: $id, timeInterval:$timeInterval)
      }`;

    const variables = {
      id: recordToDelete.id,
      groupName: recordToDelete.groupName,
      timeInterval: recordToDelete.timeInterval,
    };

    await axios
      .post(
        apiUrl,
        { query, variables },
        {
          headers: {
            Authorization: accessToken,
          },
        }
      )
      .then((response) => {
        if (
          response.data.errors &&
          response.data.errors[0].errorType == "Unauthorized"
        )
          setError(
            "Delete access is not granted to your account for this resource."
          );
      })
      .catch((error) => {
        console.log(error);
      });
    if (!error) {
      const query1 = `
        query MyQuery ($GroupName:String!){
          getAvailableEndpoints(GroupName:$GroupName){
            EndpointName
            id
            }
            }`;

      const variables1 = {
        GroupName: recordToDelete.groupName,
      };

      const responseId = await axios.post(
        apiUrl,
        { query: query1, variables: variables1 },
        {
          headers: {
            Authorization: accessToken,
          },
        }
      );

      const rqdData = responseId.data.data.getAvailableEndpoints;

      const addEndpoints = async (id) => {
        const query = `
              mutation MyMutation( $id: String!) {
                updateEndpointGroup(GroupName:"default", id: $id)
                }`;
        const variables = { id };
        await axios.post(
          apiUrl,
          { query, variables },
          {
            headers: {
              Authorization: accessToken,
            },
          }
        );
      };

      rqdData.forEach((endpoint) => {
        addEndpoints(endpoint.id);
      });
    }
    setOpenDialog(false);
    setRefresh(!refresh);
    setRecordToDelete({
      id: null,
      groupName: null,
      timeInterval: null,
    });
    setIsSubmitting(false);
  };

  const displayData = showAll ? data : data.slice(0, 4);

  return (
    <>
      <div className="bg-white rounded-lg shadow-lg border border-gray-200">
        {error && (
          <ErrorNotification message={error} onClose={() => setError(null)} />
        )}
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-2xl font-bold text-[#003366]">
              Endpoint Groups
            </h3>
            <button
              onClick={handleClickOpen}
              className="flex items-center gap-2 px-4 py-2 bg-[#003366] text-white rounded hover:bg-[#004488]"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              Add Group
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-[#003366]/5">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#003366] uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#003366] uppercase tracking-wider">
                    Time Interval
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-[#003366] uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {displayData.map((row, i) => (
                  <tr key={i} className="hover:bg-[#003366]/5">
                    <td className="px-6 py-4 whitespace-nowrap text-[#003366]">
                      {row.GroupName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-700">
                      {row.TimeInterval}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      {row.GroupName === "default" ? (
                        <>Cannot Modify</>
                      ) : (
                        <>
                          <button
                            onClick={() =>
                              handleDeleteConfirmation(
                                row.id,
                                row.GroupName,
                                row.TimeInterval
                              )
                            }
                            className="text-red-600 hover:text-red-800 pr-2"
                            onMouseEnter={(e) => {
                              const rect =
                                e.currentTarget.getBoundingClientRect();
                              setTooltip({
                                visible: true,
                                x: rect.left + rect.width / 2,
                                y: rect.bottom + 5,
                                text: "Delete",
                              });
                            }}
                            onMouseLeave={() =>
                              setTooltip({
                                visible: false,
                                x: 0,
                                y: 0,
                                text: "",
                              })
                            }
                          >
                            <svg
                              className="w-5 h-5"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                              />
                            </svg>
                          </button>
                          <button
                            onClick={() =>
                              handleClickOpenEdit(row.GroupName, row.id)
                            }
                            className="text-blue-600 hover:text-blue-800"
                            onMouseEnter={(e) => {
                              const rect =
                                e.currentTarget.getBoundingClientRect();
                              setTooltip({
                                visible: true,
                                x: rect.left + rect.width / 2,
                                y: rect.bottom + 5,
                                text: "Modify",
                              });
                            }}
                            onMouseLeave={() =>
                              setTooltip({
                                visible: false,
                                x: 0,
                                y: 0,
                                text: "",
                              })
                            }
                          >
                            <Cog className="w-5 h-5" />
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Show More/Less button */}
            {!loading && data.length > 4 && (
              <div className="text-center mt-4">
                <button
                  onClick={() => setShowAll(!showAll)}
                  className="text-[#003366] hover:text-[#004488] font-medium"
                >
                  {showAll ? "Show Less" : "Show More"}
                </button>
              </div>
            )}

            {/* Loading State */}
            {loading && (
              <div className="flex justify-center items-center py-16">
                <CircularProgress size={40} style={{ color: "#003366" }} />
              </div>
            )}

            {/* No Data State - only show when not loading */}
            {!loading && data.length === 0 && (
              <div className="text-center py-12">
                <div className="text-[#003366]">
                  <svg
                    className="w-12 h-12 mx-auto mb-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M20 12H4"
                    />
                  </svg>
                  <p className="text-lg font-semibold">No Data Available</p>
                  <p className="mt-2">
                    Click the Add New button to add your first item
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {tooltip.visible && (
        <div
          style={{
            position: "fixed",
            left: `${tooltip.x}px`,
            top: `${tooltip.y}px`,
            transform: "translateX(-50%)",
            zIndex: 1000,
          }}
          className="bg-black text-white text-sm rounded px-2 py-1"
        >
          {tooltip.text}
        </div>
      )}

      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{
          style: {
            borderRadius: "8px",
            padding: "8px",
            maxWidth: "450px",
          },
        }}
      >
        <DialogTitle
          id="alert-dialog-title"
          style={{
            padding: "16px 24px",
            fontSize: "1.25rem",
            fontWeight: 600,
            color: "#003366",
          }}
        >
          {"Confirm Deletion"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            id="alert-dialog-description"
            style={{
              color: "#4a4a4a",
              fontSize: "1rem",
              marginBottom: "8px",
            }}
          >
            Are you sure you want to delete the record{" "}
            <b>{recordToDelete.groupName}</b>?
          </DialogContentText>
        </DialogContent>
        <DialogActions style={{ padding: "16px 24px", gap: "12px" }}>
          <button
            onClick={() => setOpenDialog(false)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "8px 16px",
              backgroundColor: "#FFD700",
              color: "#003366",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "0.875rem",
              fontWeight: 500,
              transition: "background-color 0.2s",
            }}
            onMouseOver={(e) =>
              (e.currentTarget.style.backgroundColor = "#FFE44D")
            }
            onMouseOut={(e) =>
              (e.currentTarget.style.backgroundColor = "#FFD700")
            }
          >
            <X size={16} />
            Cancel
          </button>
          <button
            onClick={deleteRecord}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "8px 16px",
              backgroundColor: "#003366",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "0.875rem",
              fontWeight: 500,
              transition: "background-color 0.2s",
            }}
            onMouseOver={(e) =>
              (e.currentTarget.style.backgroundColor = "#004488")
            }
            onMouseOut={(e) =>
              (e.currentTarget.style.backgroundColor = "#003366")
            }
            autoFocus
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <Save size={16} />
            )}
            Confirm
          </button>
        </DialogActions>
      </Dialog>

      <Dialog open={open} onClose={handleClose}>
        <AddEndpointGroupForm
          close={handleClose}
          refresh={refresh}
          setRefresh={setRefresh}
          error={error}
          setError={setError}
        />
      </Dialog>

      <Dialog open={openEdit} onClose={handleCloseEdit}>
        <EditEndpointGroupForm
          close={handleCloseEdit}
          groupName={selectedGroupName}
          id={selectedGroupId}
          refresh={refresh}
          setRefresh={setRefresh}
          error={error}
          setError={setError}
        />
      </Dialog>
    </>
  );
}